import {
  AlertCircle,
  Building,
  Edit,
  Mail,
  MapPin,
  Receipt,
  RefreshCw,
  Save,
  User,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { billingService } from '../services/billingService'
import type {
  BillingContact,
  UpdateBillingContactRequest,
} from '../types/billing.types'

interface BillingContactCardProps {
  contact: BillingContact
  onContactUpdated?: (contact: BillingContact) => void
}

export function BillingContactCard({
  contact: initialContact,
  onContactUpdated,
}: BillingContactCardProps) {
  const [open, setOpen] = useState(false)
  const [contact, setContact] = useState<BillingContact>(initialContact)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: string, value: string) => {
    setContact(prev => ({
      ...prev,
      [field]: value,
    }))
    // Clear errors for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleAddressChange = (field: string, value: string) => {
    setContact(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!contact.name?.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!contact.email?.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!contact.address?.street?.trim()) {
      newErrors.street = 'Street address is required'
    }

    if (!contact.address?.city?.trim()) {
      newErrors.city = 'City is required'
    }

    if (!contact.address?.postal_code?.trim()) {
      newErrors.postal_code = 'Postal code is required'
    }

    if (!contact.address?.country?.trim()) {
      newErrors.country = 'Country is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const updateData: UpdateBillingContactRequest = {
        name: contact.name,
        email: contact.email,
        company: contact.company,
        address: contact.address,
        tax_id: contact.tax_id,
      }

      const updatedContact =
        await billingService.updateBillingContact(updateData)
      onContactUpdated?.(updatedContact)
      setOpen(false)
    } catch (error) {
      toast.error(extractErrorMessage(error))
      setErrors({
        submit: 'Failed to update billing contact. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !loading) {
      setContact(initialContact) // Reset form
      setErrors({})
    }
    setOpen(newOpen)
  }

  return (
    <Card className='bg-transparent border'>
      <CardContent className='p-6'>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Receipt className='w-5 h-5 text-green-600' />
              <h3 className='text-lg font-medium'>Billing Contact</h3>
            </div>
            <Dialog open={open} onOpenChange={handleOpenChange}>
              <DialogTrigger asChild>
                <Button variant='outline' size='sm'>
                  <Edit className='mr-2 h-4 w-4' />
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent className='sm:max-w-[500px]'>
                <DialogHeader>
                  <DialogTitle>Update Billing Contact</DialogTitle>
                  <DialogDescription>
                    Update the contact information for billing and invoices.
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4 py-4'>
                  {/* Name and Email */}
                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='name'>
                        Name <span className='text-red-500'>*</span>
                      </Label>
                      <Input
                        id='name'
                        value={contact.name}
                        onChange={e =>
                          handleInputChange('name', e.target.value)
                        }
                        placeholder='Full name'
                        className={errors.name ? 'border-red-500' : ''}
                      />
                      {errors.name && (
                        <div className='flex items-center gap-1 text-sm text-red-600'>
                          <AlertCircle className='w-4 h-4' />
                          {errors.name}
                        </div>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='email'>
                        Email <span className='text-red-500'>*</span>
                      </Label>
                      <Input
                        id='email'
                        type='email'
                        value={contact.email}
                        onChange={e =>
                          handleInputChange('email', e.target.value)
                        }
                        placeholder='billing@company.com'
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && (
                        <div className='flex items-center gap-1 text-sm text-red-600'>
                          <AlertCircle className='w-4 h-4' />
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company */}
                  <div className='space-y-2'>
                    <Label htmlFor='company'>Company</Label>
                    <Input
                      id='company'
                      value={contact.company || ''}
                      onChange={e =>
                        handleInputChange('company', e.target.value)
                      }
                      placeholder='Company name'
                    />
                  </div>

                  {/* Address */}
                  <div className='space-y-2'>
                    <Label>Address</Label>
                    <div className='space-y-3'>
                      <Input
                        placeholder='Street address *'
                        value={contact.address?.street || ''}
                        onChange={e =>
                          handleAddressChange('street', e.target.value)
                        }
                        className={errors.street ? 'border-red-500' : ''}
                      />
                      {errors.street && (
                        <div className='flex items-center gap-1 text-sm text-red-600'>
                          <AlertCircle className='w-4 h-4' />
                          {errors.street}
                        </div>
                      )}

                      <div className='grid gap-3 md:grid-cols-2'>
                        <Input
                          placeholder='City *'
                          value={contact.address?.city || ''}
                          onChange={e =>
                            handleAddressChange('city', e.target.value)
                          }
                          className={errors.city ? 'border-red-500' : ''}
                        />
                        <Input
                          placeholder='State/Province'
                          value={contact.address?.state || ''}
                          onChange={e =>
                            handleAddressChange('state', e.target.value)
                          }
                        />
                      </div>
                      {errors.city && (
                        <div className='flex items-center gap-1 text-sm text-red-600'>
                          <AlertCircle className='w-4 h-4' />
                          {errors.city}
                        </div>
                      )}

                      <div className='grid gap-3 md:grid-cols-2'>
                        <Input
                          placeholder='Postal Code *'
                          value={contact.address?.postal_code || ''}
                          onChange={e =>
                            handleAddressChange('postal_code', e.target.value)
                          }
                          className={errors.postal_code ? 'border-red-500' : ''}
                        />
                        <Input
                          placeholder='Country *'
                          value={contact.address?.country || ''}
                          onChange={e =>
                            handleAddressChange('country', e.target.value)
                          }
                          className={errors.country ? 'border-red-500' : ''}
                        />
                      </div>
                      {(errors.postal_code || errors.country) && (
                        <div className='flex items-center gap-1 text-sm text-red-600'>
                          <AlertCircle className='w-4 h-4' />
                          {errors.postal_code || errors.country}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tax ID */}
                  <div className='space-y-2'>
                    <Label htmlFor='tax_id'>Tax ID</Label>
                    <Input
                      id='tax_id'
                      value={contact.tax_id || ''}
                      onChange={e =>
                        handleInputChange('tax_id', e.target.value)
                      }
                      placeholder='Tax identification number'
                    />
                    <p className='text-xs text-muted-foreground'>
                      Optional - for tax reporting purposes
                    </p>
                  </div>

                  {errors.submit && (
                    <div className='flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200'>
                      <AlertCircle className='w-4 h-4 text-red-600' />
                      <span className='text-sm text-red-600'>
                        {errors.submit}
                      </span>
                    </div>
                  )}
                </div>

                <div className='flex justify-end gap-3'>
                  <Button
                    variant='outline'
                    onClick={() => setOpen(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={loading}>
                    {loading ? (
                      <>
                        <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className='mr-2 h-4 w-4' />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className='space-y-4'>
            {/* Contact Info */}
            <div className='space-y-3'>
              <div className='flex items-center gap-2'>
                <User className='w-4 h-4 text-muted-foreground' />
                <span className='font-medium'>{initialContact.name}</span>
              </div>

              <div className='flex items-center gap-2'>
                <Mail className='w-4 h-4 text-muted-foreground' />
                <span className='text-muted-foreground'>
                  {initialContact.email}
                </span>
              </div>

              {initialContact.company && (
                <div className='flex items-center gap-2'>
                  <Building className='w-4 h-4 text-muted-foreground' />
                  <span className='text-muted-foreground'>
                    {initialContact.company}
                  </span>
                </div>
              )}
            </div>

            {/* Address */}
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <MapPin className='w-4 h-4 text-muted-foreground' />
                <span className='font-medium text-sm'>Billing Address</span>
              </div>
              <div className='ml-6 text-sm text-muted-foreground space-y-1'>
                <div>{initialContact.address.street}</div>
                <div>
                  {initialContact.address.city}
                  {initialContact.address.state &&
                    `, ${initialContact.address.state}`}{' '}
                  {initialContact.address.postal_code}
                </div>
                <div>{initialContact.address.country}</div>
              </div>
            </div>

            {/* Tax ID */}
            {initialContact.tax_id && (
              <div className='text-sm'>
                <span className='font-medium'>Tax ID:</span>
                <span className='text-muted-foreground ml-2'>
                  {initialContact.tax_id}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
